import {
  createContext,
  useCallback,
  useContext,
  useSyncExternalStore,
  type ReactNode,
} from 'react'

type Listener = () => void

function normalizeId(id: string | number | null | undefined): string | null {
  if (id == null || id === '') return null
  return String(id)
}

function createAgentCreatedStore() {
  const activeIds = new Set<string>()
  const timers = new Map<string, ReturnType<typeof setTimeout>>()
  const listenersById = new Map<string, Set<Listener>>()

  function emit(id: string) {
    const listeners = listenersById.get(id)
    if (!listeners) return
    listeners.forEach((l) => l())
  }

  function subscribeId(id: string, listener: Listener) {
    let listeners = listenersById.get(id)
    if (!listeners) {
      listeners = new Set<Listener>()
      listenersById.set(id, listeners)
    }
    listeners.add(listener)
    return () => {
      const current = listenersById.get(id)
      if (!current) return
      current.delete(listener)
      if (!current.size) listenersById.delete(id)
    }
  }

  function isCreated(id: string) {
    return activeIds.has(id)
  }

  function markCreated(rawId: string | number) {
    const id = normalizeId(rawId)
    if (!id) return
    activeIds.add(id)
    const existing = timers.get(id)
    if (existing) clearTimeout(existing)
    emit(id)
    timers.set(
      id,
      setTimeout(() => {
        activeIds.delete(id)
        timers.delete(id)
        emit(id)
      }, 8000),
    )
  }

  return { subscribeId, isCreated, markCreated }
}

const store = createAgentCreatedStore()
const AgentCreatedCtx = createContext(store)

export function AgentCreatedProvider({ children }: { children: ReactNode }) {
  return <AgentCreatedCtx.Provider value={store}>{children}</AgentCreatedCtx.Provider>
}

export function useMarkAgentCreated() {
  return useContext(AgentCreatedCtx).markCreated
}

/** Per-ID subscription — only the specific item re-renders. */
export function useIsAgentCreated(objectId: string | number | null | undefined) {
  const ctx = useContext(AgentCreatedCtx)
  const id = normalizeId(objectId)

  const subscribe = useCallback(
    (listener: Listener) => {
      if (!id) return () => {}
      return ctx.subscribeId(id, listener)
    },
    [ctx, id],
  )

  const getSnapshot = useCallback(() => {
    if (!id) return false
    return ctx.isCreated(id)
  }, [ctx, id])

  return useSyncExternalStore(subscribe, getSnapshot, () => false)
}
